<template>
  <div class="widget campaign welcome">
    <p-progress-linear v-if="vm.isLoading" indeterminate></p-progress-linear>
    <p-form v-if="!vm.isLoading && _.isEmpty(vm.error) && !vm.isSubmitted" :model="vm.formPayload" :schema="vm.formPayload.schema" class="wrapper">
      <div class="preference wrapper">
        <h4>{{ $t('widget.welcome.preferences.title') }}</h4>
        <div class="marketing">
          <p-checkbox-group v-model="vm.formPayload.newsletterItems" :label="$t('widget.welcome.preferences.label.marketing')" :items="vm.newsletterItems"></p-checkbox-group>
        </div>
        <div class="gift">
          <p-checkbox-group v-model="vm.formPayload.characteristicItems" :label="$t('widget.welcome.preferences.label.gift')" :items="vm.characteristicItems"></p-checkbox-group>
        </div>
        <div class="motivation">
          <p-fieldset :label="$t('widget.welcome.preferences.label.motivation')">
            <p-row column>
              <p-radio-field
                v-for="(item, i) in vm.motivationItems"
                :key="i + item.id"
                name="motivation"
                :value="item.id"
                :label="item.caption"
                @change="vm.selectMotivation($event, item.id)"
              ></p-radio-field>
            </p-row>
          </p-fieldset>
        </div>
      </div>
      <div class="contact wrapper">
        <h4>{{ $t('widget.welcome.contact.title') }}</h4>
        <p-row>
          <p-col xs12 md6>
            <p-text-field v-model="vm.formPayload.contact.firstName" :label="$t('core.address.firstName')" :disabled="vm.isContactDataPrefilled"></p-text-field>
          </p-col>
          <p-col xs12 md6>
            <p-text-field v-model="vm.formPayload.contact.lastName" :label="$t('core.address.lastName')" :disabled="vm.isContactDataPrefilled"></p-text-field>
          </p-col>
        </p-row>
        <p-row>
          <p-col xs12 md10>
            <p-text-field v-model="vm.formPayload.contact.street" :label="$t('core.address.street')" required></p-text-field>
          </p-col>
          <p-col xs12 md2>
            <p-text-field v-model="vm.formPayload.contact.houseNumber" :label="$t('core.address.houseNumber')"></p-text-field>
          </p-col>
        </p-row>
        <p-row>
          <p-col xs12 md2>
            <p-text-field v-model="vm.formPayload.contact.postalCode" autocomplete="postal-code" :label="$t('core.address.postalCode')" required></p-text-field>
          </p-col>
          <p-col xs12 md10>
            <p-text-field v-model="vm.formPayload.contact.city" :label="$t('core.address.city')" required></p-text-field>
          </p-col>
        </p-row>
        <p-row>
          <p-col xs12>
            <p-text-field v-model="vm.formPayload.contact.email" :label="$t('core.address.email')" required></p-text-field>
          </p-col>
        </p-row>
        <p-row>
          <p-col xs12>
            <p-phone-field v-model="vm.formPayload.contact.phoneNumber" :label="$t('core.address.phoneNumber')"></p-phone-field>
          </p-col>
          <p-col xs12>
            <p-phone-field v-model="vm.formPayload.contact.mobilePhoneNumber" :label="$t('core.address.mobilePhoneNumber')"></p-phone-field>
          </p-col>
        </p-row>
      </div>
    </p-form>
    <p-request-alert
      v-if="_.isSet(vm.error)"
      v-model="vm.error"
      class="mb-3 mt-3"
    ></p-request-alert>
    <p-alert type="success" class="mb-3 mt-3" :value="vm.isSubmitted">
      {{ $t('widget.welcome.successMessage') }}
    </p-alert>
    <p-row v-if="!vm.isLoading">
      <p-col v-if="!vm.isSubmitted" justify-end>
        <p-button :disabled="!vm.canSubmitForm" :loading="vm.isSubmitting" @click="vm.submitForm()">
          {{ $t('core.app.send') }}
        </p-button>
      </p-col>
      <p-col v-else justify-start>
        <p-button @click="windowBack">
          {{ $t('core.app.back') }}
        </p-button>
      </p-col>
    </p-row>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import CampaignEventVM from './welcome.vue.model';

  export default Vue.extend({
    layout: 'layout-widget',
    props: {
      contactId: { default: undefined, type: String },
      tenantKey: { default: undefined, type: String },
      mountId: { default: undefined, type: String },
      submitRedirectUrl: { default: undefined, type: String },
    },
    data: () => ({
      vm: new CampaignEventVM(),
      formSummary: {},
    }),
    computed: {
      // read from widget config
      innerContactId(): string {
        if (this._.isSet(this.contactId)) {
          return this.contactId;
        }
        if (this.$config.values['widget-dynamicform-contactid']) {
          return this.$config.values['widget-dynamicform-contactid'];
        }
        const queries = this.$routerUtils.getQueryParams();
        return queries.contactid as string;
      },
      innerTenantKey(): string {
        if (this._.isSet(this.tenantKey)) {
          return this.tenantKey.toLowerCase();
        }
        if (this.$config.values['widget-tenant-key']) {
          return this.$config.values['widget-tenant-key'];
        }
        const queries = this.$routerUtils.getQueryParams();
        if (queries.tenantKey) { return queries.tenantKey?.toLowerCase() as string; }
        return 'kls';
      },
      innerRedirectUrl(): string {
        if (this._.isSet(this.submitRedirectUrl)) {
          return this.submitRedirectUrl.toLowerCase();
        }
        if (this.$config.values['widget-submit-redirect-url']) {
          return this.$config.values['widget-submit-redirect-url'];
        }
        const queries = this.$routerUtils.getQueryParams();
        if (queries.submitRedirectUrl) { return queries.submitRedirectUrl.toLowerCase() as string; }
        return '';
      },
    },
    async mounted() {
      this.vm.contactId = this.innerContactId;
      this.vm.tenantKey = this.innerTenantKey;
      this.vm.submitRedirectUrl = this.innerRedirectUrl;
      await this.vm.initialize();
    },
    methods: {
      windowBack() {
        let backFunc = this.$router.back.bind(this.$router);
        if (this._.isSet(window?.history)) {
          backFunc = window.history.back.bind(window.history);
        } else if (typeof (window as any)?.back === 'function') {
          backFunc = (window as any).back.bind(window);
        }
        backFunc();
      },
    },
  });

</script>
